import { renumberRows, type DraftRowShape } from './draftRow';
import { duplicateKeyFor, type DuplicateCheck } from './markDuplicateRows';

/**
 * Merge freshly uploaded rows into what is on screen (drafts or seeds): a row
 * matching an on-screen row by natural key updates it in place — keeping its
 * `entityId`, so Weiter still classifies it as an update of the imported
 * entity — and only genuinely new rows append. This makes re-uploading the
 * same sheet idempotent instead of stacking duplicates, mirroring the
 * collision→update semantics of the initialize endpoints (ADR 0011).
 *
 * Each on-screen row is matched at most once per upload, so a sheet that
 * itself contains duplicates keeps them visible for the Weiter duplicate pass.
 *
 * `mergeRow` customizes how a match is applied — the users step keeps a
 * protected row's locked identity fields and takes only the non-empty
 * placement fields from the upload. The default replaces the row, keeping its
 * `entityId`.
 */
export function upsertUploadedRows<TRow extends DraftRowShape<unknown>>(
  currentRows: TRow[],
  uploadedRows: TRow[],
  checks: Array<DuplicateCheck<TRow['data']>>,
  mergeRow: (current: TRow, uploaded: TRow) => TRow = (current, uploaded) => ({
    ...uploaded,
    entityId: current.entityId
  })
): TRow[] {
  // Per check, queue the on-screen row indices behind each natural key so
  // repeated keys pair one-to-one in order.
  const indexQueues = checks.map((check) => {
    const queue = new Map<string, number[]>();
    currentRows.forEach((row, index) => {
      const key = duplicateKeyFor(check, row.data);
      if (!key) return;
      const indices = queue.get(key);
      if (indices) {
        indices.push(index);
      } else {
        queue.set(key, [index]);
      }
    });
    return queue;
  });

  const consumed = new Set<number>();
  const claimMatch = (data: TRow['data']): number | undefined => {
    for (const [checkIndex, check] of checks.entries()) {
      const key = duplicateKeyFor(check, data);
      const queue = key ? indexQueues[checkIndex].get(key) : undefined;
      while (queue && queue.length > 0) {
        const index = queue.shift()!;
        if (!consumed.has(index)) {
          consumed.add(index);
          return index;
        }
      }
    }
    return undefined;
  };

  const merged = [...currentRows];
  for (const uploaded of uploadedRows) {
    const matchIndex = claimMatch(uploaded.data);
    if (matchIndex === undefined) {
      merged.push(uploaded);
    } else {
      merged[matchIndex] = mergeRow(merged[matchIndex], uploaded);
    }
  }

  return renumberRows(merged);
}
